import { useState } from 'react'
import { Track, DryRunResult, YardOccupancy } from '../../../types/interchange'

interface PredictiveCapacityPanelProps {
  availableTracks?: Track[]
  selectedTrack?: Track | null
  dryRunResult?: DryRunResult | null
  yardOccupancy?: YardOccupancy | null
  pendingAssetIds?: string[]
  processedAssetIds?: string[]
}

export default function PredictiveCapacityPanel({ availableTracks, selectedTrack, dryRunResult, yardOccupancy, pendingAssetIds = [], processedAssetIds = [] }: PredictiveCapacityPanelProps) {
  const [showDetails, setShowDetails] = useState(false)

  const getProgressGradient = (occupied: number, clearanceCapacity: number, physicalLength: number) => {
    const clearancePercent = (clearanceCapacity / physicalLength) * 100
    const occupiedPercent = (occupied / physicalLength) * 100

    if (occupiedPercent <= clearancePercent * 0.7) {
      // Well within clearance - green
      return 'bg-green-500'
    } else if (occupiedPercent <= clearancePercent * 0.85) {
      // Approaching clearance - green to yellow gradient
      return 'bg-gradient-to-r from-green-500 via-green-400 to-yellow-500'
    } else if (occupiedPercent <= clearancePercent) {
      // Near/at clearance - yellow to orange gradient
      return 'bg-gradient-to-r from-yellow-500 via-orange-400 to-orange-500'
    } else {
      // Beyond clearance - orange to red gradient
      return 'bg-gradient-to-r from-orange-500 via-red-400 to-red-500'
    }
  }

  const isBeyondClearance = (occupied: number, clearanceCapacity: number) => {
    return occupied > clearanceCapacity
  }

  // Use yard occupancy from API if available, otherwise fallback to calculation
  const yardMetrics = yardOccupancy || {
    totalPhysicalLength: availableTracks?.reduce((sum, track) => sum + (track.totalPhysicalLengthFeet || 0), 0) || 0,
    totalClearanceCapacity: availableTracks?.reduce((sum, track) => sum + (track.clearanceCapacityFeet || 0), 0) || 0,
    totalOccupied: 0,
    utilization: 0
  }

  // Calculate selected track utilization from real occupancy data
  const calculateSelectedTrackMetrics = () => {
    if (!selectedTrack) return null
    const occupied = selectedTrack.currentOccupiedFeet ?? 0
    const utilization = selectedTrack.currentUtilization ?? 0
    return { occupied, utilization }
  }

  const selectedTrackMetrics = calculateSelectedTrackMetrics()

  return (
    <div className="flex flex-col h-full">
      <h3 className="font-bold text-muted-foreground shrink-0">Global Yard Capacity & Predictive Utilization</h3>

      <div className="flex-1 overflow-y-auto flex flex-col gap-4">
        {/* Section 1: Occupancy (Top Box) */}
        <div className="p-4 border rounded-lg bg-card shrink-0 mt-4">
          <h4 className="font-semibold text-foreground mb-3">Occupancy</h4>
        
        {/* Overall Utilization - Always visible */}
        <div className="mb-4">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-muted-foreground">Overall Utilization</span>
            <span className="text-foreground font-medium">
              {yardMetrics.totalOccupied.toFixed(0)} / {yardMetrics.totalClearanceCapacity.toFixed(0)} usable ft
            </span>
          </div>
          <div className={`relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 ${isBeyondClearance(yardMetrics.totalOccupied, yardMetrics.totalClearanceCapacity) ? 'animate-pulse shadow-[0_0_12px_#f43f5e] midnight:shadow-[0_0_12px_#dc2626] cyber:shadow-[0_0_12px_#dc2626] foundry:shadow-[0_0_12px_#dc2626] daylight:shadow-[0_0_12px_#f43f5e]' : ''}`}>
            <div
              className={`h-4 rounded-full transition-all duration-300 ${getProgressGradient(yardMetrics.totalOccupied, yardMetrics.totalClearanceCapacity, yardMetrics.totalPhysicalLength)}`}
              style={{ width: `${(yardMetrics.totalOccupied / yardMetrics.totalPhysicalLength) * 100}%` }}
            />
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-rose-500 shadow-[0_0_8px_#f43f5e] z-10"
              style={{ left: `${(yardMetrics.totalClearanceCapacity / yardMetrics.totalPhysicalLength) * 100}%` }}
            />
            {(() => {
              const ceilingPct = (yardMetrics.totalClearanceCapacity / yardMetrics.totalPhysicalLength) * 100
              // Centered translate bleeds past the container when the
              // marker nears an edge — anchor inside instead.
              const anchoredRight = ceilingPct > 75
              const anchoredLeft = ceilingPct < 15
              return (
                <div
                  className="absolute -bottom-4 text-xs font-bold text-rose-500 tracking-wider uppercase whitespace-nowrap"
                  style={
                    anchoredRight
                      ? { right: 0 }
                      : anchoredLeft
                        ? { left: 0 }
                        : { left: `${ceilingPct}%`, transform: 'translateX(-50%)' }
                  }
                >
                  CLEARANCE CEILING
                </div>
              )
            })()}
          </div>
          <div className="flex justify-between text-sm mt-5">
            <span className="text-muted-foreground" title="Yard-internal storage tracks only (CLASSIFICATION etc.)">Storage Track Capacity: {yardMetrics.totalPhysicalLength.toFixed(0)}ft</span>
            <span className="text-foreground">{yardMetrics.utilization.toFixed(1)}% Usable Capacity Used</span>
          </div>
        </div>

        {/* Customer-serving bucket — SPUR + INDUSTRY_SIDING, separate from yard-internal storage (AD-8) */}
        {yardOccupancy?.customerSpurOccupancy && (
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Industry & Spur Capacity</span>
              <span className="text-foreground font-medium">
                {(yardOccupancy.customerSpurOccupancy.occupiedFeet ?? 0).toFixed(0)} / {(yardOccupancy.customerSpurOccupancy.capacityFeet ?? 0).toFixed(0)} ft
                · {yardOccupancy.customerSpurOccupancy.carCount ?? 0} cars on {yardOccupancy.customerSpurOccupancy.trackCount ?? 0} tracks
              </span>
            </div>
            <div className="relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getProgressGradient(yardOccupancy.customerSpurOccupancy.occupiedFeet ?? 0, yardOccupancy.customerSpurOccupancy.capacityFeet ?? 0, yardOccupancy.customerSpurOccupancy.capacityFeet || 1)}`}
                style={{ width: `${Math.min(100, ((yardOccupancy.customerSpurOccupancy.occupiedFeet ?? 0) / (yardOccupancy.customerSpurOccupancy.capacityFeet || 1)) * 100)}%` }}
              />
            </div>
            <div className="flex justify-end text-sm mt-1">
              <span className="text-foreground">{(yardOccupancy.customerSpurOccupancy.utilization ?? 0).toFixed(1)}% Used</span>
            </div>
          </div>
        )}
        <p className="text-xs text-muted-foreground mb-2">
          Excludes Mainlines, Leads, and Bad-Order tracks per AD-8 metrics.
        </p>

        <div className="border-t border-border" />

        {/* Selected Track Utilization - Conditional */}
        {selectedTrack && selectedTrackMetrics ? (
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">{selectedTrack.name} Utilization</span>
              <span className="text-foreground font-medium">
                {selectedTrackMetrics.occupied.toFixed(0)} / {selectedTrack.clearanceCapacityFeet?.toFixed(0) || 0} usable ft
              </span>
            </div>
            <div className={`relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 ${isBeyondClearance(selectedTrackMetrics.occupied, selectedTrack.clearanceCapacityFeet || 0) ? 'animate-pulse shadow-[0_0_12px_#f43f5e] midnight:shadow-[0_0_12px_#dc2626] cyber:shadow-[0_0_12px_#dc2626] foundry:shadow-[0_0_12px_#dc2626] daylight:shadow-[0_0_12px_#f43f5e]' : ''}`}>
              <div
                className={`h-4 rounded-full transition-all duration-300 ${getProgressGradient(selectedTrackMetrics.occupied, selectedTrack.clearanceCapacityFeet || 0, selectedTrack.totalPhysicalLengthFeet || 1)}`}
                style={{ width: `${(selectedTrackMetrics.occupied / (selectedTrack.totalPhysicalLengthFeet || 1)) * 100}%` }}
              />
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-rose-500 shadow-[0_0_8px_#f43f5e] z-10"
                style={{ left: `${((selectedTrack.clearanceCapacityFeet || 0) / (selectedTrack.totalPhysicalLengthFeet || 1)) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-sm mt-1">
              <span className="text-muted-foreground">Track Physical Length: {selectedTrack.totalPhysicalLengthFeet?.toFixed(0) || 0}ft</span>
              <span className="text-foreground">{selectedTrackMetrics.utilization.toFixed(1)}% Track Capacity Used</span>
            </div>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-2 text-sm">
            Select a track to view specific utilization.
          </div>
        )}
      </div>

      {/* Section 2: Predictive Capacity (Bottom Box) - Always visible */}
      <div className="p-4 border rounded-lg bg-card border-blue-200 dark:border-blue-800 flex-1 flex flex-col">
        <h4 className="font-semibold text-foreground mb-3 text-blue-600 dark:text-blue-400">
          Predictive Capacity (Assessment)
        </h4>
        
        {dryRunResult && dryRunResult.metadata ? (
          <div className="flex flex-col gap-4">
            {/* Critical Metrics Row */}
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Target Track:</span>
                <span className="text-foreground font-medium">{dryRunResult.metadata.trackName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Validation Status:</span>
                <span className={`font-semibold ${dryRunResult.valid ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {dryRunResult.valid ? '✓ PASSED' : '✗ FAILED'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Projected Footprint:</span>
                <span className="text-foreground">{dryRunResult.metadata.totalFootprint?.toFixed(0) || 0}ft</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Remaining After Ingest:</span>
                <span className="text-foreground font-medium">{dryRunResult.metadata.remainingCapacity?.toFixed(0) || 0}ft</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className={`relative w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 ${isBeyondClearance((dryRunResult.metadata.existingOccupancy || 0) + (dryRunResult.metadata.totalFootprint || 0), dryRunResult.metadata.trackCapacity || 0) ? 'animate-pulse shadow-[0_0_12px_#f43f5e] midnight:shadow-[0_0_12px_#dc2626] cyber:shadow-[0_0_12px_#dc2626] foundry:shadow-[0_0_12px_#dc2626] daylight:shadow-[0_0_12px_#f43f5e]' : ''}`}>
              <div
                className={`h-4 rounded-full transition-all duration-300 ${getProgressGradient((dryRunResult.metadata.existingOccupancy || 0) + (dryRunResult.metadata.totalFootprint || 0), dryRunResult.metadata.trackCapacity || 0, selectedTrack?.totalPhysicalLengthFeet || 1)}`}
                style={{ width: `${Math.min(((dryRunResult.metadata.existingOccupancy || 0) + (dryRunResult.metadata.totalFootprint || 0)) / (selectedTrack?.totalPhysicalLengthFeet || 1) * 100, 100)}%` }}
              />
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-rose-500 shadow-[0_0_8px_#f43f5e] z-10"
                style={{ left: `${(dryRunResult.metadata.trackCapacity || 0) / (selectedTrack?.totalPhysicalLengthFeet || 1) * 100}%` }}
              />
            </div>

            {/* Collapsible Details */}
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              {showDetails ? '▼ Hide Details' : '▶ Show Details'}
            </button>

            {showDetails && (
              <div className="space-y-2 text-sm pt-2 border-t border-border">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Track Capacity:</span>
                  <span className="text-foreground">{dryRunResult.metadata.trackCapacity?.toFixed(0) || 0}ft</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Existing Occupancy:</span>
                  <span className="text-foreground">{dryRunResult.metadata.existingOccupancy?.toFixed(0) || 0}ft</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reserved Capacity:</span>
                  <span className="text-foreground">{dryRunResult.metadata.reservedCapacity?.toFixed(0) || 0}ft</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <div className="text-muted-foreground text-sm">
              {processedAssetIds.length > 0 && pendingAssetIds.length > 0
                ? "Cuts pending for next pass — run 'Predictive Path Assessment' to check capacity, then 'Confirm Pass'"
                : "No active assessment — select a track and run the Predictive Path Assessment."
              }
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  )
}
